import { type ReactNode } from 'react'
import { motion } from 'motion/react'

interface HrSectionProps {
  title: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
}

// Consistent tab-content wrapper. Header + thin divider + body. Used by every
// tab so density and rhythm stay identical. Subtle enter animation (NOT a slide
// — per Codex risk: avoid full-page slides on tab change to prevent layout-
// shift + focus loss on long content).
export function HrSection({ title, subtitle, actions, children }: HrSectionProps) {
  return (
    <motion.section
      key={title}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
      className="space-y-6"
    >
      <header className="flex items-start justify-between gap-4 pb-3 border-b border-outline-variant/40">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-on-surface tracking-tight">{title}</h2>
          {subtitle ? (
            <p className="text-sm text-on-surface-variant mt-0.5">{subtitle}</p>
          ) : null}
        </div>
        {actions ? <div className="flex-shrink-0">{actions}</div> : null}
      </header>
      <div>{children}</div>
    </motion.section>
  )
}
